import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'

import { Info, Save, Plus, X, UserPlus, UserCheck, Search, FilePlus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAuthStore } from '@/store/auth'
import { TicketService } from '@/lib/services/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useParentTicketDraftStore } from '@/store/parentTicketDraft'
import { useParentTicketStore } from '@/store/parentTicket'
import { apiClient } from '@/lib/api/client'
import type {
  AgentOption,
  ClientOption,
  FieldEngineerRecommendation,
  ParentTicketFormValues,
  RequestOption,
} from '@/types/ticket'
import { CreateTicketRequestModal } from '@/components/forms/CreateTicketRequestModal'

export type ParentTicketFormProps = {
  /** When set, form is in edit mode: load ticket and update on submit */
  parentTicketId?: string
  onSuccess?: () => void
  onCancel?: () => void
}

const severityOptions = ['1', '2', '3', '4', '5'] as const
const supportTypeOptions = [
  { value: 'l1_basic', label: 'L1 - Basic troubleshooting' },
  { value: 'l1_remote', label: 'L1 - 1.5 Remote support' },
  { value: 'l2_network', label: 'L2 - Network/Advanced support' },
  { value: 'l3_field', label: 'L3 - Client Support/Field Support' },
] as const

const PERMIT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif']
const PERMIT_IMAGE_MAX_MB = 5

function filterPermitImageFiles(files: FileList | File[]): File[] {
  const list = Array.isArray(files) ? files : Array.from(files)
  return list.filter((f) => PERMIT_IMAGE_TYPES.includes(f.type))
}

function agentLabel(a: AgentOption) {
  const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim()
  return name || a.user_login || `User #${a.id}`
}

function fmtDatetimeLocal(iso: string) {
  try {
    const d = new Date(iso)
    const day = d.getDate()
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = monthNames[d.getMonth()]
    const year = d.getFullYear()
    const hours = d.getHours()
    const minutes = d.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    const displayHours = hours % 12 || 12
    const displayMinutes = minutes.toString().padStart(2, '0')
    return `${day} ${month} ${year} - ${displayHours}:${displayMinutes} ${ampm}`
  } catch {
    return iso
  }
}

/** Convert ISO or datetime string to YYYY-MM-DDTHH:mm for datetime-local input value */
function toDatetimeLocalValue(iso: string | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const h = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    return `${y}-${m}-${day}T${h}:${min}`
  } catch {
    return ''
  }
}

/** Display for selected request: "Request ID | Contact Name | Store" e.g. ALS-RQ-20260217-CE2FB1 | Contact LENOVO | Lenovo */
function requestLabel(r: RequestOption) {
  const bits = [
    r.id,
    r.contactName || r.clientName,
    r.clientOrg,
  ].filter(Boolean)
  return bits.join(' | ')
}

/**
 * Red section title + bordered container
 * IMPORTANT:
 * - container bg is same as page bg (so it won’t look like a white card)
 * - only inputs are white
 */
function Section({
  title,
  children,
  sectionBg,
  rightAction,
}: {
  title: string
  children: React.ReactNode
  sectionBg: string
  rightAction?: React.ReactNode
}) {
  return (
    <Box>
      <HStack justify="space-between" align="center" mb={2}>
        <Text fontWeight="700" color="red.700" fontSize="md">
          {title}
        </Text>
        {rightAction ?? null}
      </HStack>

      <Box
        // section container
        p={4}
        bg={sectionBg} 
      >
        {children}
      </Box>
    </Box>
  )
}

/** Generate a unique ticket ID for new tickets (agent can share before submit). */
function generateTicketId(): string {
  const now = new Date()
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  const hh = String(now.getHours()).padStart(2, '0')
  const min = String(now.getMinutes()).padStart(2, '0')
  const ss = String(now.getSeconds()).padStart(2, '0')
  const dateTime = `${yyyy}${mm}${dd}${hh}${min}${ss}`
  const random = Math.random().toString(36).slice(2, 8).toUpperCase()
  return `PT-${dateTime}-${random}`
}

// Create Asset Modal Component
type CreateAssetModalProps = {
  isOpen: boolean
  onClose: () => void
  onSuccess: (asset: any) => void
  clientCode?: string
}

function CreateAssetModal({ isOpen, onClose, onSuccess, clientCode }: CreateAssetModalProps) {
  const colors = useThemeColors()
  const { showError, showSuccess } = useToastNotification()
  const [brands, setBrands] = useState<any[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [functions, setFunctions] = useState<any[]>([])
  const [brandModels, setBrandModels] = useState<any[]>([])
  const [specificCategories, setSpecificCategories] = useState<any[]>([])
  
  const [assetForm, setAssetForm] = useState({
    brand_id: '',
    category_id: '',
    tag_number: '',
    model: '',
    serial_number: '',
    function: '',
    manufacturer: '',
    sw_provider: '',
    warranty_date: '',
    order_received_date: '',
    ram: '',
    disk_space: '',
    os_version: '',
    ms_office: '',
    processor: '',
    cpu: '',
    ip_address: '',
    mac_address: '',
    active: true,
  })

  // Helper to get options for specific category dropdowns
  const getSpecificCategoryOptions = (categoryName: string) => {
    if (!categoryName || !specificCategories || !Array.isArray(specificCategories)) {
      return []
    }
    
    // Map category names to their parent IDs
    const categoryParentMap: Record<string, number> = {
      'SW Provider': 111,
      'Disk Space': 112,
      'MS Office': 113,
      'RAM': 114
    }
    
    const parentId = categoryParentMap[categoryName]
    if (!parentId) {
      return []
    }
    
    // Filter categories by parent_id and ensure name exists and is not null
    const filteredCategories = specificCategories.filter(cat => {
      return cat && 
             cat.name && 
             Number(cat.parent_id) === Number(parentId)
    })
    
    // Sort by name in ascending order
    return filteredCategories.sort((a, b) => {
      if (!a.name || !b.name) return 0
      return a.name.localeCompare(b.name)
    })
  }

  // Load brands and specific categories on mount
  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setAssetForm({
        brand_id: '',
        category_id: '',
        tag_number: '',
        model: '',
        serial_number: '',
        function: '',
        manufacturer: '',
        sw_provider: '',
        warranty_date: '',
        order_received_date: '',
        ram: '',
        disk_space: '',
        os_version: '',
        ms_office: '',
        processor: '',
        cpu: '',
        ip_address: '',
        mac_address: '',
        active: true,
      })
      setCategories([])
      setFunctions([])
      setBrandModels([])

      const config: any = {}
      if (clientCode) {
        config.headers = { 'X-Client-Code': clientCode }
      } else {
        // If no clientCode, show warning
        console.warn('CreateAssetModal: No clientCode provided. Dropdowns may not load correctly.')
      }
      
      // Load brands
      apiClient.get('/options/brands', config)
        .then((response) => {
          const data = response.data
          console.log('Brands response:', data)
          const brandsArray = Array.isArray(data) ? data : (data?.data || [])
          console.log('Brands array:', brandsArray)
          setBrands(brandsArray)
        })
        .catch((error) => {
          console.error('Error loading brands:', error)
          console.error('Error response:', error?.response)
          showError(error?.response?.data?.message || 'Failed to load brands. Please ensure a request is selected.')
          setBrands([])
        })
      
      // Load specific categories
      apiClient.get('/options/specific-categories', config)
        .then((response) => {
          const data = response.data
          console.log('Specific categories response:', data)
          
          // Handle different response structures
          let categoriesArray: any[] = []
          if (Array.isArray(data)) {
            categoriesArray = data
          } else if (data?.data && Array.isArray(data.data)) {
            categoriesArray = data.data
          } else if (data?.data && typeof data.data === 'object') {
            categoriesArray = Object.values(data.data)
          }
          
          console.log('Specific categories array before filter:', categoriesArray)
          
          // Filter out entries with null/empty names
          const validCategories = categoriesArray.filter((cat: any) => {
            const isValid = cat && cat.name && cat.name.trim() !== '' && cat.name !== null && cat.name !== 'null'
            if (!isValid) {
              console.log('Filtered out invalid category:', cat)
            }
            return isValid
          })
          
          console.log('Valid specific categories after filter:', validCategories)
          setSpecificCategories(validCategories)
        })
        .catch((error) => {
          console.error('Error loading specific categories:', error)
          console.error('Error response:', error?.response)
          showError(error?.response?.data?.message || 'Failed to load categories. Please ensure a request is selected.')
          setSpecificCategories([])
        })
    }
  }, [isOpen, clientCode])

  // Load categories when brand changes
  useEffect(() => {
    if (assetForm.brand_id && brands.length > 0) {
      const selectedBrand = brands.find(b => b.id.toString() === assetForm.brand_id)
      if (selectedBrand?.categories) {
        try {
          const categoriesData = JSON.parse(selectedBrand.categories)
          setCategories(categoriesData)
        } catch {
          setCategories([])
        }
      } else {
        setCategories([])
      }
      setAssetForm(prev => ({ ...prev, category_id: '', model: '' }))
    }
  }, [assetForm.brand_id, brands])

  // Load functions when category changes
  useEffect(() => {
    if (assetForm.category_id) {
      console.log('Loading functions for category_id:', assetForm.category_id)
      const config: any = {}
      if (clientCode) {
        config.headers = { 'X-Client-Code': clientCode }
      }
      
      apiClient.get(`/options/functions/${assetForm.category_id}`, config)
        .then((response) => {
          console.log('Functions API response:', response)
          const responseData = response.data
          console.log('Functions response data:', responseData)
          console.log('Functions API URL:', `/options/functions/${assetForm.category_id}`)
          
          // Handle different response structures
          let functionsArray: any[] = []
          if (Array.isArray(responseData)) {
            functionsArray = responseData
          } else if (responseData?.data && Array.isArray(responseData.data)) {
            functionsArray = responseData.data
          } else if (responseData?.data && typeof responseData.data === 'object') {
            // If data is an object, try to extract array from it
            functionsArray = Object.values(responseData.data)
          }
          
          console.log('Functions array before filter:', functionsArray)
          
          // Filter out null/undefined entries and ensure name exists and is not empty
          const validFunctions = functionsArray.filter((f: any) => {
            const isValid = f && f.name && f.name.trim() !== '' && f.name !== null && f.name !== 'null'
            if (!isValid) {
              console.log('Filtered out invalid function:', f)
            }
            return isValid
          })
          
          console.log('Valid functions after filter:', validFunctions)
          setFunctions(validFunctions)
        })
        .catch((error) => {
          console.error('Error loading functions:', error)
          console.error('Error response:', error?.response)
          showError(error?.response?.data?.message || 'Failed to load functions')
          setFunctions([])
        })
    } else {
      setFunctions([])
    }
  }, [assetForm.category_id, clientCode])

  // Load brand models when brand and category change
  useEffect(() => {
    if (assetForm.brand_id && assetForm.category_id) {
      const config: any = {
        params: { brand_id: assetForm.brand_id, category_id: assetForm.category_id },
      }
      if (clientCode) {
        config.headers = { 'X-Client-Code': clientCode }
      }
      
      apiClient.get('/options/brand-models', config)
        .then(({ data }) => {
          const modelsArray = Array.isArray(data) ? data : (data?.data || [])
          setBrandModels(modelsArray)
        })
        .catch((error) => {
          console.error('Error loading brand models:', error)
          showError(error?.response?.data?.message || 'Failed to load brand models')
          setBrandModels([])
        })
    } else {
      setBrandModels([])
    }
  }, [assetForm.brand_id, assetForm.category_id, clientCode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    if (!assetForm.brand_id || !assetForm.category_id || !assetForm.tag_number || !assetForm.model || !assetForm.serial_number) {
      showError('Please fill in all required fields (Brand, Category, Tag Number, Model, Serial Number)')
      return
    }

    // Get brand name and category name from the arrays
    const selectedBrand = brands.find(b => b.id.toString() === assetForm.brand_id.toString())
    const selectedCategory = categories.find(c => c.id.toString() === assetForm.category_id.toString())
    
    // Create a temporary asset object (not saved to database)
    // Use negative timestamp as temporary ID to avoid conflicts with real database IDs
    const tempAsset = {
      id: -Date.now(), // Temporary negative ID
      tag_number: assetForm.tag_number,
      model: assetForm.model,
      serial_number: assetForm.serial_number,
      brand_name: selectedBrand?.name || selectedBrand?.label || '',
      category_name: selectedCategory?.name || selectedCategory?.label || '',
      function: assetForm.function || null,
      brand_id: assetForm.brand_id,
      category_id: assetForm.category_id,
      manufacturer: assetForm.manufacturer || null,
      sw_provider: assetForm.sw_provider || null,
      warranty_date: assetForm.warranty_date || null,
      order_received_date: assetForm.order_received_date || null,
      ram: assetForm.ram || null,
      disk_space: assetForm.disk_space || null,
      os_version: assetForm.os_version || null,
      ms_office: assetForm.ms_office || null,
      processor: assetForm.processor || null,
      cpu: assetForm.cpu || null,
      ip_address: assetForm.ip_address || null,
      mac_address: assetForm.mac_address || null,
      active: assetForm.active,
      // Mark as temporary (not saved to database)
      _isTemporary: true,
    }
    
    showSuccess('Asset added to list')
    onSuccess(tempAsset)
    
    // Reset form
    setAssetForm({
      brand_id: '',
      category_id: '',
      tag_number: '',
      model: '',
      serial_number: '',
      function: '',
      manufacturer: '',
      sw_provider: '',
      warranty_date: '',
      order_received_date: '',
      ram: '',
      disk_space: '',
      os_version: '',
      ms_office: '',
      processor: '',
      cpu: '',
      ip_address: '',
      mac_address: '',
      active: true,
    })
    setCategories([])
    setFunctions([])
    setBrandModels([])
  }

  const inputBaseProps = {
    bg: 'white',
    borderColor: colors.inputBorder ?? 'gray.200',
    borderRadius: 'lg',
    _hover: { borderColor: colors.inputborderHover ?? colors.inputBorder ?? 'gray.300' },
    _focusVisible: {
      borderColor: colors.primary ?? 'blue.500',
      boxShadow: `0 0 0 1px ${colors.primary ?? '#3182ce'}`,
    },
  } as const

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
          <ModalContent maxH="90vh">
        <ModalHeader>Create New Asset</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody maxH="calc(90vh - 140px)" overflowY="auto">
            {!clientCode && (
              <Alert status="warning" mb={4} size="sm">
                <AlertIcon />
                <AlertDescription fontSize="xs">
                  Please select a request first to ensure dropdowns load correctly.
                </AlertDescription>
              </Alert>
            )}
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontSize="sm">Brand</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.brand_id}
                  onChange={(e) => setAssetForm({ ...assetForm, brand_id: e.target.value, category_id: '', model: '' })}
                  {...inputBaseProps}
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id.toString()}>
                      {brand.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Category / Machine Type</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.category_id}
                  onChange={(e) => setAssetForm({ ...assetForm, category_id: e.target.value, model: '' })}
                  isDisabled={!assetForm.brand_id}
                  {...inputBaseProps}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id.toString()}>
                      {category?.name ?? category?.label}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Asset Tag / POS TID Number</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.tag_number}
                  onChange={(e) => setAssetForm({ ...assetForm, tag_number: e.target.value.toUpperCase() })}
                  placeholder="e.g. POS-2024-001 or NA"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Model</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.model}
                  onChange={(e) => setAssetForm({ ...assetForm, model: e.target.value })}
                  isDisabled={!assetForm.brand_id || !assetForm.category_id}
                  {...inputBaseProps}
                >
                  <option value="">Select Model</option>
                  {brandModels.map(model => (
                    <option key={model.id} value={model.name}>
                      {model.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Serial Number</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.serial_number}
                  onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })}
                  placeholder="e.g. SN123456789"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Function</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.function}
                  onChange={(e) => setAssetForm({ ...assetForm, function: e.target.value })}
                  isDisabled={!assetForm.category_id}
                  {...inputBaseProps}
                >
                  <option value="">Select Function</option>
                  {functions.filter(f => f && f.name).map(func => (
                    <option key={func.name} value={func.name.toString()}>
                      {func.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Manufacturer</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.manufacturer}
                  onChange={(e) => setAssetForm({ ...assetForm, manufacturer: e.target.value })}
                  placeholder="e.g. Ingenico, Verifone, PAX"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Warranty Expiration Date</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={assetForm.warranty_date}
                  onChange={(e) => setAssetForm({ ...assetForm, warranty_date: e.target.value })}
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Order Received Date</FormLabel>
                <Input
                  size="sm"
                  type="date"
                  value={assetForm.order_received_date}
                  onChange={(e) => setAssetForm({ ...assetForm, order_received_date: e.target.value })}
                  {...inputBaseProps}
                />
              </FormControl>
            </VStack>

            <Divider my={4} />

            <Heading size="sm" mb={4} color={colors.heading}>
              Asset Specifications
            </Heading>

            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">RAM</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.ram}
                  onChange={(e) => setAssetForm({ ...assetForm, ram: e.target.value })}
                  {...inputBaseProps}
                >
                  <option value="">Select RAM</option>
                  {getSpecificCategoryOptions('RAM')
                    .filter(ram => ram && ram.name)
                    .sort((a, b) => {
                      // Extract numeric value from RAM names (e.g., "8GB" -> 8)
                      const getNumericValue = (name: string) => {
                        const match = name.match(/(\d+)/)
                        return match ? parseInt(match[1]) : 0
                      }
                      return getNumericValue(a.name) - getNumericValue(b.name)
                    })
                    .map(ram => (
                      <option key={ram.id} value={ram.name}>
                        {ram.name}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">DISK SPACE</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.disk_space}
                  onChange={(e) => setAssetForm({ ...assetForm, disk_space: e.target.value })}
                  {...inputBaseProps}
                >
                  <option value="">Select Disk Space</option>
                  {getSpecificCategoryOptions('Disk Space')
                    .filter(disk => disk && disk.name)
                    .sort((a, b) => {
                      // Extract numeric value from Disk Space names (e.g., "500GB" -> 500)
                      const getNumericValue = (name: string) => {
                        const match = name.match(/(\d+)/)
                        return match ? parseInt(match[1]) : 0
                      }
                      return getNumericValue(a.name) - getNumericValue(b.name)
                    })
                    .map(disk => (
                      <option key={disk.id} value={disk.name}>
                        {disk.name}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">OS VERSION</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.os_version}
                  onChange={(e) => setAssetForm({ ...assetForm, os_version: e.target.value })}
                  placeholder="e.g. Windows 10 Pro, Ubuntu 22.04"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">MS OFFICE</FormLabel>
                <Select
                  size="sm"
                  value={assetForm.ms_office}
                  onChange={(e) => setAssetForm({ ...assetForm, ms_office: e.target.value })}
                  {...inputBaseProps}
                >
                  <option value="">Select MS Office</option>
                  {getSpecificCategoryOptions('MS Office')
                    .filter(office => office && office.name)
                    .map(office => (
                      <option key={office.id} value={office.name}>
                        {office.name}
                      </option>
                    ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">PROCESSOR</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.processor}
                  onChange={(e) => setAssetForm({ ...assetForm, processor: e.target.value })}
                  placeholder="e.g. Intel Core i7, AMD Ryzen 5"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">CPU</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.cpu}
                  onChange={(e) => setAssetForm({ ...assetForm, cpu: e.target.value })}
                  placeholder="e.g. 2.4GHz Quad-Core"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">IP ADDRESS</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.ip_address}
                  onChange={(e) => setAssetForm({ ...assetForm, ip_address: e.target.value })}
                  placeholder="e.g. 192.168.1.100"
                  {...inputBaseProps}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">MAC ADDRESS</FormLabel>
                <Input
                  size="sm"
                  value={assetForm.mac_address}
                  onChange={(e) => setAssetForm({ ...assetForm, mac_address: e.target.value })}
                  placeholder="e.g. 00:1A:2B:3C:4D:5E"
                  {...inputBaseProps}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={2}>
              <Button type="button" onClick={onClose} variant="ghost">
                Cancel
              </Button>
              <Button type="submit" colorScheme="green">
                Add to List
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

function createInitialFormState(): ParentTicketFormValues {
  return {
    requestId: '',
    storeId: undefined,
    storeCode: '',
    ticketId: '',
    type: '',
    subject: '',
    priority: '',
    severity: '3',
    supportType: '',
    source: '',
    issueDescription: '',
    agentId: '',
    primaryEngineerId: undefined,
    backupEngineerId: undefined,
    scheduledAt: '',
    notes: '',
    workPermit: 'no_need',
    workPermitFile: null,
    assets: [],
  }
}

export function ParentTicketForm({
  parentTicketId,
  onSuccess,
  onCancel,
}: ParentTicketFormProps = {}) {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const currentUser = useAuthStore((s) => s.user)
  const getTicketById = useParentTicketStore((s) => s.getTicketById)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingMode, setSavingMode] = useState<'draft' | 'submit' | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const isEditMode = !!parentTicketId

  const [requests, setRequests] = useState<RequestOption[]>([])
  const [errors, setErrors] = useState<Record<string, string[]>>({})

  // Request search states
  const [requestSearchTerm, setRequestSearchTerm] = useState('')
  const [requestSearchResults, setRequestSearchResults] = useState<RequestOption[]>([])
  const [isRequestSearching, setIsRequestSearching] = useState(false)
  const [selectedRequestOption, setSelectedRequestOption] = useState<RequestOption | null>(null)
  const requestSearchInputRef = useRef<HTMLInputElement>(null)
  const requestSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const selectedRequestDetailsRef = useRef<HTMLDivElement>(null)

  // Store search states
  const [storeSearchTerm, setStoreSearchTerm] = useState('')
  const [storeSearchResults, setStoreSearchResults] = useState<ClientOption[]>([])
  const [isStoreSearching, setIsStoreSearching] = useState(false)
  const [selectedStoreOption, setSelectedStoreOption] = useState<ClientOption | null>(null)
  const storeSearchInputRef = useRef<HTMLInputElement>(null)
  const storeSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Agent search states (Helpdesk users)
  const [agentSearchTerm, setAgentSearchTerm] = useState('')
  const [agentSearchResults, setAgentSearchResults] = useState<AgentOption[]>([])
  const [isAgentSearching, setIsAgentSearching] = useState(false)
  const [selectedAgentOption, setSelectedAgentOption] = useState<AgentOption | null>(null)
  const [assignToMe, setAssignToMe] = useState(false)
  const agentSearchInputRef = useRef<HTMLInputElement>(null)
  const agentSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Field Engineer Recommender states
  const [fieldEngineerRecommendations, setFieldEngineerRecommendations] = useState<FieldEngineerRecommendation[]>([])
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false)

  // Asset search states
  const { isOpen: isAssetModalOpen, onOpen: onAssetModalOpen, onClose: onAssetModalClose } = useDisclosure()
  const { isOpen: isCreateAssetModalOpen, onOpen: onCreateAssetModalOpen, onClose: onCreateAssetModalClose } = useDisclosure()
  const { isOpen: isAddRequestModalOpen, onOpen: onAddRequestModalOpen, onClose: onAddRequestModalClose } = useDisclosure()
  const [assetSearchTerm, setAssetSearchTerm] = useState('')
  const [assetSearchResults, setAssetSearchResults] = useState<any[]>([])
  const [isAssetSearching, setIsAssetSearching] = useState(false)
  const [selectedAssets, setSelectedAssets] = useState<any[]>([])
  const selectedAssetsRef = useRef<any[]>([]) // Ref to track latest selectedAssets for reliable access
  const assetSearchInputRef = useRef<HTMLInputElement>(null)
  const assetSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  
  // Keep ref in sync with state
  useEffect(() => {
    selectedAssetsRef.current = selectedAssets
  }, [selectedAssets])

  // Asset search handler
  const searchAssets = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setAssetSearchResults([])
      return
    }
    
    // Get client code from selected request (similar to store search)
    const clientCode = selectedRequestOption?.clientCode
    
    // Get store ID from selected store to filter assets
    const storeId = selectedStoreOption?.storeId
    
    setIsAssetSearching(true)
    try {
      const response = await TicketService.searchAssets(searchTerm, clientCode, storeId)
      if (response.success && response.data) {
        setAssetSearchResults(response.data)
      } else {
        setAssetSearchResults([])
        if (response.message && !response.message.includes('No assets found')) {
          showError(response.message)
        }
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to search assets')
      setAssetSearchResults([])
    } finally {
      setIsAssetSearching(false)
    }
  }, [showError, selectedRequestOption?.clientCode, selectedStoreOption?.storeId])

  // Handle asset search input change
  const handleAssetSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAssetSearchTerm(value)

    // Clear previous timeout
    if (assetSearchTimeoutRef.current) {
      clearTimeout(assetSearchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    assetSearchTimeoutRef.current = setTimeout(() => {
      searchAssets(value)
    }, 300)
  }

  // Handle asset selection
  const handleAssetSelect = (asset: any) => {
    // Check if asset is already selected
    if (selectedAssets.some(a => a.id === asset.id)) {
      return
    }
    const newAssets = [...selectedAssets, asset]
    setSelectedAssets(newAssets)
    selectedAssetsRef.current = newAssets // Update ref immediately
    setForm(prev => ({ ...prev, assets: newAssets }))
    console.log('Asset added - newAssets:', newAssets)
    // Don't clear search term or results - allow multiple selections
  }

  // Handle removing asset from selected list
  const handleRemoveAsset = (assetId: number) => {
    const newAssets = selectedAssets.filter(a => a.id !== assetId)
    setSelectedAssets(newAssets)
    selectedAssetsRef.current = newAssets // Update ref immediately
    setForm(prev => ({ ...prev, assets: newAssets }))
    console.log('Asset removed - newAssets:', newAssets)
  }

  // Handle assigning field engineer from recommendations
  const handleAssignFieldEngineer = useCallback((fe: FieldEngineerRecommendation, isPrimary: boolean) => {
    const userId = fe.user_id?.toString() || fe.field_engineer_id.toString()
    
    setForm((prev) => {
      const currentPrimary = prev.primaryEngineerId?.toString()
      const currentBackup = prev.backupEngineerId?.toString()
      
      if (isPrimary) {
        // If already primary, remove assignment
        if (currentPrimary === userId) {
          showSuccess('Field Engineer Removed', `${fe.full_name} removed as Primary Engineer`)
          return {
            ...prev,
            primaryEngineerId: undefined,
          }
        }
        // If currently backup, swap to primary
        if (currentBackup === userId) {
          showSuccess('Field Engineer Assigned', `${fe.full_name} assigned as Primary Engineer`)
          return {
            ...prev,
            primaryEngineerId: userId,
            backupEngineerId: currentPrimary || undefined,
          }
        }
        // Assign as primary (may replace existing primary)
        showSuccess('Field Engineer Assigned', `${fe.full_name} assigned as Primary Engineer`)
        return {
          ...prev,
          primaryEngineerId: userId,
        }
      } else {
        // If already backup, remove assignment
        if (currentBackup === userId) {
          showSuccess('Field Engineer Removed', `${fe.full_name} removed as Backup Engineer`)
          return {
            ...prev,
            backupEngineerId: undefined,
          }
        }
        // If currently primary, swap to backup
        if (currentPrimary === userId) {
          showSuccess('Field Engineer Assigned', `${fe.full_name} assigned as Backup Engineer`)
          return {
            ...prev,
            backupEngineerId: userId,
            primaryEngineerId: currentBackup || undefined,
          }
        }
        // Assign as backup (may replace existing backup)
        showSuccess('Field Engineer Assigned', `${fe.full_name} assigned as Backup Engineer`)
        return {
          ...prev,
          backupEngineerId: userId,
        }
      }
    })
  }, [showSuccess])

  const permitFileInputRef = useRef<HTMLInputElement>(null)
  const [isDraggingPermit, setIsDraggingPermit] = useState(false)

  // Page + section bg
  const pageBg = colors.bg ?? 'gray.50'
  const sectionBg = pageBg // keep same

  const [form, setForm] = useState<ParentTicketFormValues>(createInitialFormState)
  const draft = useParentTicketDraftStore((s) => s.draft)
  const hydratedFromRequestId = useParentTicketDraftStore(
    (s) => s.hydratedFromRequestId,
  )
  const [appliedDraftRequestId, setAppliedDraftRequestId] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    if (!hydratedFromRequestId) return
    if (appliedDraftRequestId === hydratedFromRequestId) return

    setForm((prev) => ({
      ...prev,
      requestId: draft.requestId || prev.requestId,
      issueDescription: draft.issueDescription || prev.issueDescription,
    }))

    setAppliedDraftRequestId(hydratedFromRequestId)
  }, [appliedDraftRequestId, draft, hydratedFromRequestId])

  useEffect(() => {
    void bootstrap()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Create mode: generate unique ticket ID once so agent can copy/send before submit
  useEffect(() => {
    if (parentTicketId) return
    setForm((prev) => ({
      ...prev,
      ticketId: prev.ticketId || generateTicketId(),
    }))
  }, [parentTicketId])

  // Edit mode: populate form from ticket (all details for display/edit)
  useEffect(() => {
    if (!parentTicketId) return
    const ticket = getTicketById(parentTicketId)
    if (!ticket) return
    const ext = ticket as Record<string, unknown>
    const subject =
      (ext.subject as string) ||
      [ticket.requestId, ticket.clientName, ticket.clientOrg].filter(Boolean).join(' | ') ||
      ''
    const storeId = (ticket as { store_id?: number }).store_id
    const storeCode = (ticket as { store_code?: string }).store_code ?? ''
    const agentId = ext.agentId != null ? String(ext.agentId) : undefined
    const rawPrimary = ext.primaryEngineerId ?? ext.primary_engineer_id
    const primaryEngineerId =
      typeof rawPrimary === 'number' || typeof rawPrimary === 'string' ? rawPrimary : undefined
    const rawBackup = ext.backupEngineerId ?? ext.backup_engineer_id
    const backupEngineerId =
      typeof rawBackup === 'number' || typeof rawBackup === 'string' ? rawBackup : undefined
    const scheduledAt = (ext.scheduledAt ?? ext.scheduled_at) as string | undefined
    const notes = ext.notes as string | undefined
    const workPermit = (ext.workPermit ?? ext.work_permit) as string | undefined
    const workPermitPath = (ext.work_permit_path ?? ext.workPermitPath) as string | undefined
    const source = (ext.source as string) ?? undefined
    let assets = ext.assets
    if (typeof assets === 'string') {
      try {
        assets = JSON.parse(assets) as unknown[]
      } catch {
        assets = []
      }
    }
    if (!Array.isArray(assets)) assets = []

    const displayTicketId = (ext.ticket_number ?? ext.ticketId ?? ticket.id) as string
    const ticketType = (ext.type as string) ?? ''
    setForm((prev) => ({
      ...prev,
      requestId: ticket.requestId || prev.requestId,
      ticketId: displayTicketId,
      type: ticketType || prev.type,
      storeId: storeId ?? prev.storeId,
      storeCode: storeCode || prev.storeCode,
      subject: subject || prev.subject,
      priority: (ticket.severity === 'Mid' ? 'Medium' : ticket.severity) || prev.priority,
      severity: String(ticket.severity ?? ticket.priority ?? '3').replace(/^Severity\s+/i, '') || '3',
      supportType: (ticket as { support_type?: string }).support_type ?? prev.supportType ?? '',
      source: source ?? prev.source,
      issueDescription: ticket.description || prev.issueDescription,
      agentId: agentId ?? prev.agentId,
      primaryEngineerId: (primaryEngineerId ?? prev.primaryEngineerId) as string | number | undefined,
      backupEngineerId: (backupEngineerId ?? prev.backupEngineerId) as string | number | undefined,
      scheduledAt: scheduledAt ?? prev.scheduledAt,
      notes: (notes != null ? String(notes) : prev.notes) ?? '',
      workPermit: workPermit ?? prev.workPermit ?? 'no_need',
      // Keep user-selected File; only set from ticket path when user has not chosen a new file (so upload works)
      workPermitFile: prev.workPermitFile instanceof File ? prev.workPermitFile : (workPermitPath ?? prev.workPermitFile ?? null),
      assets: (Array.isArray(assets) ? assets : prev.assets ?? []) as ParentTicketFormValues['assets'],
    }))
    if (ticket.requestId) setRequestSearchTerm(requestLabel({ id: ticket.requestId, clientName: ticket.clientName, clientOrg: ticket.clientOrg, requestedAt: ticket.requestedAt }))
    if (storeCode) setStoreSearchTerm(`[${storeCode}]`)
    if (ticket.agentName) setAgentSearchTerm(ticket.agentName)
    // Always set selectedAssets from loaded assets (even if empty) to ensure it's in sync
    if (Array.isArray(assets)) {
      setSelectedAssets(assets as any[])
      selectedAssetsRef.current = assets as any[] // Update ref immediately
    } else {
      setSelectedAssets([])
      selectedAssetsRef.current = []
    }
  }, [parentTicketId, getTicketById])

  // Note: We removed the useEffect that synced selectedAssets from form.assets
  // This was causing issues where user selections were being overwritten.
  // selectedAssets is now the single source of truth for UI display.
  // form.assets is synced FROM selectedAssets when submitting (in handleSubmit).

  // Edit mode: set selected agent from ticket.agent when present so "Agent" block displays on first load
  useEffect(() => {
    if (!parentTicketId) return
    const ticket = getTicketById(parentTicketId) as { agent?: import('@/types/ticket').AgentOption }
    if (!ticket?.agent || typeof ticket.agent !== 'object' || !('id' in ticket.agent)) return
    setSelectedAgentOption(ticket.agent)
    setAgentSearchTerm(agentLabel(ticket.agent))
    const isCurrentUser = currentUser && Number(ticket.agent.id) === currentUser.id
    setAssignToMe(!!isCurrentUser)
  }, [parentTicketId, getTicketById, currentUser?.id])

  // Edit mode: load and select the ticket request so "Request Information" is displayed (same as create)
  useEffect(() => {
    if (!parentTicketId) return
    const ticket = getTicketById(parentTicketId)
    if (!ticket?.requestId) return

    const ticketWithRequest = ticket as { request?: RequestOption }
    if (ticketWithRequest.request && typeof ticketWithRequest.request === 'object' && 'id' in ticketWithRequest.request) {
      setSelectedRequestOption(ticketWithRequest.request)
      setRequestSearchTerm(requestLabel(ticketWithRequest.request))
      return
    }

    const syntheticRequest: RequestOption = {
      id: ticket.requestId,
      clientName: ticket.clientName,
      clientOrg: ticket.clientOrg ?? undefined,
      requestedAt: ticket.requestedAt,
      issueDescription: ticket.description ?? undefined,
      description: ticket.description ?? undefined,
    }

    if (ticket.requestId.length < 3) {
      setSelectedRequestOption(syntheticRequest)
      setRequestSearchTerm(requestLabel(syntheticRequest))
      return
    }

    let cancelled = false
    TicketService.searchRequestOptions(ticket.requestId)
      .then((res) => {
        if (cancelled || !res.success || !res.data?.length) {
          setSelectedRequestOption(syntheticRequest)
          setRequestSearchTerm(requestLabel(syntheticRequest))
          return
        }
        const match = res.data.find((r) => String(r.id) === String(ticket.requestId))
        const selected = match ?? syntheticRequest
        setSelectedRequestOption(selected)
        setRequestSearchTerm(requestLabel(selected))
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedRequestOption(syntheticRequest)
          setRequestSearchTerm(requestLabel(syntheticRequest))
        }
      })
    return () => {
      cancelled = true
    }
  }, [parentTicketId, getTicketById])

  // Create mode: when redirected from IncomingRequest, same process as search + click – fetch request from API then set selection so details and fields match
  useEffect(() => {
    if (parentTicketId || !hydratedFromRequestId || !draft.requestId) return

    const syntheticRequest: RequestOption = {
      id: draft.requestId,
      clientName: draft.requestorName,
      clientOrg: draft.clientOrg || undefined,
      serialNumber: draft.serialNumber || undefined,
      unitModel: draft.unitModelRecord || undefined,
      requestedAt: draft.requestDatetime,
      issueDescription: draft.issueDescription || undefined,
      description: draft.issueDescription || undefined,
      submittedAt: draft.requestDatetime || undefined,
      serviceType: draft.serviceType || undefined,
      contactName: draft.contactName || undefined,
      contactEmail: draft.contactEmail || undefined,
      requestSource: draft.requestSource || undefined,
    }

    if (draft.requestId.length < 3) {
      setSelectedRequestOption(syntheticRequest)
      setRequestSearchTerm(requestLabel(syntheticRequest))
      return
    }

    let cancelled = false
    TicketService.searchRequestOptions(draft.requestId)
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data?.length) {
          const match = res.data.find((r) => String(r.id) === String(draft.requestId))
          const selected = match ?? syntheticRequest
          setSelectedRequestOption(selected)
          setRequestSearchTerm(requestLabel(selected))
          updateField('requestId', selected.id)
          if (selectedRequestDetailsRef.current) {
            setTimeout(() => {
              selectedRequestDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
            }, 50)
          }
        } else {
          setSelectedRequestOption(syntheticRequest)
          setRequestSearchTerm(requestLabel(syntheticRequest))
          updateField('requestId', syntheticRequest.id)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedRequestOption(syntheticRequest)
          setRequestSearchTerm(requestLabel(syntheticRequest))
          updateField('requestId', syntheticRequest.id)
        }
      })
    return () => {
      cancelled = true
    }
  }, [parentTicketId, hydratedFromRequestId, draft.requestId])

  // Edit mode: load and select the store so "Store Information" is displayed (same as request details)
  useEffect(() => {
    if (!parentTicketId) return
    const ticket = getTicketById(parentTicketId)
    const storeCode = (ticket as { store_code?: string })?.store_code
    const storeId = (ticket as { store_id?: number })?.store_id
    if (!storeCode && storeId == null) return

    const ticketWithStore = ticket as { store?: import('@/types/ticket').ClientOption }
    if (ticketWithStore.store && typeof ticketWithStore.store === 'object' && 'storeCode' in ticketWithStore.store) {
      setSelectedStoreOption(ticketWithStore.store)
      setStoreSearchTerm(`[${ticketWithStore.store.storeCode}] - ${ticketWithStore.store.storeName || ''}`.trim())
      return
    }

    const clientCode = selectedRequestOption?.clientCode ?? (ticket as { request?: { clientCode?: string } })?.request?.clientCode
    const syntheticStore: import('@/types/ticket').ClientOption = {
      storeCode: storeCode || '',
      storeName: storeCode ? `Store ${storeCode}` : 'Store',
      ...(storeId != null && { storeId }),
    }

    if (!clientCode || (storeCode?.length ?? 0) < 3) {
      setSelectedStoreOption(syntheticStore)
      setStoreSearchTerm(storeCode ? `[${storeCode}]` : '')
      return
    }

    let cancelled = false
    TicketService.searchStoreOptions(storeCode ?? '', clientCode)
      .then((res) => {
        if (cancelled || !res.success || !res.data?.length) {
          setSelectedStoreOption(syntheticStore)
          setStoreSearchTerm(storeCode ? `[${storeCode}]` : '')
          return
        }
        const match = res.data.find(
          (s) => String(s.storeCode) === String(storeCode) || (storeId != null && s.storeId === storeId)
        )
        const selected = match ?? syntheticStore
        setSelectedStoreOption(selected)
        setStoreSearchTerm(`[${selected.storeCode}] - ${selected.storeName || ''}`.trim())
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedStoreOption(syntheticStore)
          setStoreSearchTerm(storeCode ? `[${storeCode}]` : '')
        }
      })
    return () => {
      cancelled = true
    }
  }, [parentTicketId, getTicketById, selectedRequestOption?.clientCode])

  const bootstrap = async () => {
    try {
      setLoading(true)
      // No need to load all clients upfront - we'll search on demand
    } catch (e: any) {
      showError(e?.message || 'Failed to load ticket data')
    } finally {
      setLoading(false)
    }
  }

  // Debounced store search function
  const searchStores = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setStoreSearchResults([])
      // Clear selected store when search term is too short
      setSelectedStoreOption(null)
      return
    }

    // Use client_code from selected request to set X-Client-Code header for store search
    // This ensures stores are searched from the correct client database
    const clientCode = selectedRequestOption?.clientCode

    setIsStoreSearching(true)
    try {
      const response = await TicketService.searchStoreOptions(searchTerm, clientCode)
      if (response.success && response.data && response.data.length > 0) {
        setStoreSearchResults(response.data)
      } else {
        setStoreSearchResults([])
        // Clear selected store when search returns empty results
        setSelectedStoreOption(null)
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to search stores')
      setStoreSearchResults([])
      setSelectedStoreOption(null)
    } finally {
      setIsStoreSearching(false)
    }
  }, [showError, selectedRequestOption?.clientCode])

  // Handle store search input change
  const handleStoreSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setStoreSearchTerm(value)

    // Clear previous timeout
    if (storeSearchTimeoutRef.current) {
      clearTimeout(storeSearchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    storeSearchTimeoutRef.current = setTimeout(() => {
      searchStores(value)
    }, 300)
  }

  // Handle store selection
  const handleStoreSelect = (store: ClientOption) => {
    setSelectedStoreOption(store)
    updateField('storeCode', store.storeCode)
    if (store.storeId != null) updateField('storeId', store.storeId)
    setStoreSearchTerm(`[${store.storeCode}] - ${store.storeName}`)
    setStoreSearchResults([])
  }

  // Debounced request search function
  const searchRequests = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setRequestSearchResults([])
      // Clear selected request when search term is too short
      setSelectedRequestOption(null)
      return
    }

    setIsRequestSearching(true)
    try {
      const response = await TicketService.searchRequestOptions(searchTerm)
      if (response.success && response.data && response.data.length > 0) {
        setRequestSearchResults(response.data)
      } else {
        setRequestSearchResults([])
        // Clear selected request when search returns empty results
        setSelectedRequestOption(null)
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to search requests')
      setRequestSearchResults([])
      setSelectedRequestOption(null)
    } finally {
      setIsRequestSearching(false)
    }
  }, [showError])

  // Handle request search input change
  const handleRequestSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setRequestSearchTerm(value)

    // Clear previous timeout
    if (requestSearchTimeoutRef.current) {
      clearTimeout(requestSearchTimeoutRef.current)
    }

    // Set new timeout for debounced search
    requestSearchTimeoutRef.current = setTimeout(() => {
      searchRequests(value)
    }, 300)
  }

  // Handle request selection (from search dropdown click) – show "Selected Request Details" and scroll it into view
  const handleRequestSelect = (request: RequestOption) => {
    setSelectedRequestOption(request)
    updateField('requestId', request.id)
    setRequestSearchTerm(requestLabel(request))
    setRequestSearchResults([])
    requestSearchInputRef.current?.blur()
    setTimeout(() => {
      selectedRequestDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 50)
  }

  // Agent search (Helpdesk users)
  const searchAgents = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setAgentSearchResults([])
      setSelectedAgentOption(null)
      return
    }
    setIsAgentSearching(true)
    try {
      const response = await TicketService.searchHelpdeskAgentOptions(searchTerm)
      if (response.success && response.data?.length) {
        setAgentSearchResults(response.data)
      } else {
        setAgentSearchResults([])
        setSelectedAgentOption(null)
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to search agents')
      setAgentSearchResults([])
      setSelectedAgentOption(null)
    } finally {
      setIsAgentSearching(false)
    }
  }, [showError])

  const handleAgentSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setAgentSearchTerm(value)
    if (agentSearchTimeoutRef.current) clearTimeout(agentSearchTimeoutRef.current)
    agentSearchTimeoutRef.current = setTimeout(() => searchAgents(value), 300)
  }

  const handleAgentSelect = (agent: AgentOption) => {
    setAssignToMe(false)
    setSelectedAgentOption(agent)
    updateField('agentId', String(agent.id))
    setAgentSearchTerm(agentLabel(agent))
    setAgentSearchResults([])
  }

  const currentUserDisplayName = currentUser
    ? [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ').trim() ||
      currentUser.name ||
      `User #${currentUser.id}`
    : ''

  const handleAssignToMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    setAssignToMe(checked)
    if (checked && currentUser) {
      updateField('agentId', String(currentUser.id))
      setAgentSearchTerm(currentUserDisplayName)
      setSelectedAgentOption(null)
      setAgentSearchResults([])
    } else {
      updateField('agentId', '')
      setAgentSearchTerm('')
      setSelectedAgentOption(null)
    }
  }

  // Field Engineer Recommender handler
  const handleRecommendFieldEngineers = useCallback(async () => {
    const storeId = selectedStoreOption?.storeId
    if (!storeId) {
      showError('Please select a store first')
      return
    }
    const clientCode = selectedRequestOption?.clientCode

    setIsLoadingRecommendations(true)
    try {
      const response = await TicketService.recommendFieldEngineers(storeId, clientCode)
      if (response.success && response.data?.length) {
        setFieldEngineerRecommendations(response.data)
      } else {
        setFieldEngineerRecommendations([])
        showError(response.message || 'No field engineers found')
      }
    } catch (error: any) {
      showError(error?.message || 'Failed to get field engineer recommendations')
      setFieldEngineerRecommendations([])
    } finally {
      setIsLoadingRecommendations(false)
    }
  }, [selectedStoreOption, selectedRequestOption, showError])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (requestSearchTimeoutRef.current) clearTimeout(requestSearchTimeoutRef.current)
      if (storeSearchTimeoutRef.current) clearTimeout(storeSearchTimeoutRef.current)
      if (agentSearchTimeoutRef.current) clearTimeout(agentSearchTimeoutRef.current)
    }
  }, [])


  const updateField = <K extends keyof ParentTicketFormValues>(
    key: K,
    value: ParentTicketFormValues[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }))

  // Permit image: preview URL for both selected File (blob) and saved path (API serve URL)
  const permitPreviewUrl = useMemo(() => {
    const file = form.workPermitFile
    if (!file) return null
    if (typeof file === 'string') {
      if (!file.trim()) return null
      const base = apiClient.defaults.baseURL || ''
      return `${base}/files/serve?path=${encodeURIComponent(file)}`
    }
    if (!(file instanceof File) || !file.type.startsWith('image/')) return null
    return URL.createObjectURL(file)
  }, [form.workPermitFile])
  useEffect(() => {
    return () => {
      if (permitPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(permitPreviewUrl)
    }
  }, [permitPreviewUrl])

  function onPermitFilesSelected(files: FileList | null) {
    if (!files?.length) return
    const filtered = filterPermitImageFiles(files)
    if (filtered.length === 0) {
      showError('Please select a JPG, PNG, or GIF image. Other file types are not accepted.')
      return
    }
    const file = filtered[0] ?? null
    updateField('workPermitFile', file)
  }
  function onPermitDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingPermit(true)
  }
  function onPermitDragLeave(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingPermit(false)
  }
  function onPermitDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingPermit(false)
    const files = e.dataTransfer?.files
    if (!files?.length) return
    onPermitFilesSelected(files)
  }

  // Auto-populate from selected request: subject (Request ID | Contact Name | Store), source, description
  useEffect(() => {
    if (!selectedRequestOption) return
    const subject = [
      selectedRequestOption.id,
      selectedRequestOption.contactName || selectedRequestOption.clientName,
      selectedRequestOption.clientOrg,
    ].filter(Boolean).join(' | ') // e.g. ALS-RQ-20260217-CE2FB1 | Contact LENOVO | Lenovo
    setForm((prev) => ({
      ...prev,
      subject: subject || prev.subject,
      source: selectedRequestOption.requestSource ?? prev.source,
      issueDescription:
        prev.issueDescription || (selectedRequestOption.issueDescription ?? selectedRequestOption.description ?? ''),
    }))
  }, [selectedRequestOption?.id])


  const handleNext = () => {
    // Validate all required fields before showing preview (Store is not required)
    const missingFields: string[] = []
    if (!form.requestId) missingFields.push('Request')
    if (!form.agentId) missingFields.push('Agent')
    if (!(form.subject ?? '').trim()) missingFields.push('Subject')
    if (!(form.severity ?? form.priority ?? '').trim()) missingFields.push('Severity')
    if (!(form.supportType ?? '').trim()) missingFields.push('Support Type')
    if (!(form.issueDescription ?? '').trim()) missingFields.push('Description')

    if (missingFields.length > 0) {
      showError(`Please fill in required fields: ${missingFields.join(', ')}`)
      return
    }

    setShowPreview(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleBack = () => {
    setShowPreview(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSubmit = async (submitTicket: boolean = false) => {
    const mode = submitTicket ? 'submit' : 'draft'
    try {
      setSaving(true)
      setSavingMode(mode)
      setErrors({})
      window.scrollTo({ top: 0, behavior: 'smooth' })

      // Use selectedAssets as the single source of truth for assets
      // Use ref to ensure we get the latest value even if state hasn't updated yet
      // selectedAssets is updated when user adds/removes assets via the UI
      // Always ensure assets is an array (never undefined or null)
      // Prefer ref if it has items, otherwise use state (state might be more up-to-date)
      const refAssets = selectedAssetsRef.current || []
      const stateAssets = selectedAssets || []
      const currentAssets = (refAssets.length > 0 ? refAssets : stateAssets)
      const assetsToSubmit = Array.isArray(currentAssets) && currentAssets.length > 0 ? currentAssets : []
      
      // Debug log to verify assets are included (can be removed in production)
      console.log('=== ASSET SUBMISSION DEBUG ===')
      console.log('selectedAssets state:', selectedAssets, 'length:', selectedAssets?.length || 0)
      console.log('selectedAssetsRef.current:', selectedAssetsRef.current, 'length:', selectedAssetsRef.current?.length || 0)
      console.log('currentAssets:', currentAssets, 'length:', currentAssets?.length || 0)
      console.log('assetsToSubmit:', assetsToSubmit, 'length:', assetsToSubmit.length)
      console.log('form.assets:', form.assets, 'length:', form.assets?.length || 0)
      console.log('=== END DEBUG ===')
      
      if (assetsToSubmit.length === 0 && (selectedAssets.length > 0 || selectedAssetsRef.current.length > 0)) {
        console.error('WARNING: Assets are empty but selectedAssets has items!', {
          selectedAssets,
          selectedAssetsRef: selectedAssetsRef.current,
          formAssets: form.assets
        })
      }
      
      // Update form.assets to keep them in sync (for future reference)
      setForm((prev) => ({ ...prev, assets: assetsToSubmit }))

      const payload = {
        ...form,
        assets: assetsToSubmit,
        submitTicket,
      }

      if (isEditMode && parentTicketId) {
        const res = await TicketService.updateParentTicket(parentTicketId, payload)
        if (!res.success) {
          if (res.errors) setErrors(res.errors)
          throw new Error(res.message || 'Failed to update ticket')
        }
        showSuccess('Success', res.message || (submitTicket ? 'Ticket submitted' : 'Ticket saved as draft'))
        onSuccess?.() ?? navigate('/tickets')
      } else {
        const res = await TicketService.createParentTicket(payload)
        if (!res.success) {
          if (res.errors) setErrors(res.errors)
          throw new Error(res.message || 'Failed to create ticket')
        }
        if (res.data?.ticketId) {
          setForm((prev) => ({ ...prev, ticketId: res.data!.ticketId }))
        }
        showSuccess(
          'Success',
          res.data?.ticketId
            ? `${submitTicket ? 'Ticket submitted' : 'Ticket saved as draft'} — Ticket ID: ${res.data.ticketId}`
            : (submitTicket ? 'Ticket submitted' : 'Ticket saved as draft'),
        )
        onSuccess?.() ?? navigate('/tickets')
      }
    } catch (e: any) {
      showError(e?.message || (isEditMode ? 'Failed to update ticket' : 'Failed to create ticket'))
    } finally {
      setSaving(false)
      setSavingMode(null)
    }
  }

  // Helper to get field engineer name (from recommendations or from API payload on edit)
  type EngineerMeta = import('@/store/parentTicket').FieldEngineerSummary
  const editTicketMeta = parentTicketId ? getTicketById(parentTicketId) as { primaryEngineer?: EngineerMeta; backupEngineer?: EngineerMeta } | undefined : undefined
  const getFieldEngineerName = (engineerId: string | number | undefined) => {
    if (!engineerId) return '-'
    const userId = engineerId.toString()
    const fe = fieldEngineerRecommendations.find(
      (f) => (f.user_id?.toString() || f.field_engineer_id.toString()) === userId
    )
    return fe?.full_name || `ID: ${engineerId}`
  }
  const primaryEngineerDisplayName = editTicketMeta?.primaryEngineer?.full_name ?? (form.primaryEngineerId ? getFieldEngineerName(form.primaryEngineerId) : null)
  const backupEngineerDisplayName = editTicketMeta?.backupEngineer?.full_name ?? (form.backupEngineerId ? getFieldEngineerName(form.backupEngineerId) : null)
  const primaryEngineerDetails = editTicketMeta?.primaryEngineer
  const backupEngineerDetails = editTicketMeta?.backupEngineer
  // Input base props
  const inputBaseProps = {
    bg: 'white',
    borderColor: colors.inputBorder ?? 'gray.200',
    borderRadius: 'lg',
    _hover: { borderColor: colors.inputborderHover ?? colors.inputBorder ?? 'gray.300' },
    _focusVisible: {
      borderColor: colors.primary ?? 'blue.500',
      boxShadow: `0 0 0 1px ${colors.primary ?? '#3182ce'}`,
    },
  } as const

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <VStack spacing={3}>
          <Spinner size="lg" color="blue.500" />
          <Text color={colors.text}>Loading ticket form...</Text>
        </VStack>
      </Box>
    )
  }

  // Preview: neutral block (no red header)
  const PreviewBlock = ({
    title,
    children,
  }: {
    title: string
    children: React.ReactNode
  }) => (
    <Box>
      <Text fontWeight="600" color={colors.textSecondary ?? 'gray.600'} fontSize="sm" mb={2}>
        {title}
      </Text>
      <Box p={4} bg={sectionBg} borderRadius="md" borderWidth="1px" borderColor={colors.border ?? 'gray.200'}>
        {children}
      </Box>
    </Box>
  )

  // Preview / Summary (Create and Edit)
  if (showPreview) {
    return (
      <Box
        p={{ base: 4, md: 6 }}
        w="100%"
        minW={0}
        bg={pageBg}
        minH="100vh"
      >
        <Divider mb={6} />
        <VStack spacing={6} align="stretch">
          <Heading size="lg" color={colors.heading}>
            Ticket Summary
          </Heading>

          {/* Complete Request Information */}
          {selectedRequestOption && (
            <PreviewBlock title="Request Information">
              <Table variant="simple" size="sm">
                <Tbody>
                  <Tr>
                    <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                      Request ID
                    </Td>
                    <Td color={colors.text} verticalAlign="top">{selectedRequestOption.id}</Td>
                  </Tr>
                  {selectedRequestOption.clientName && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Client</Td>
                      <Td color={colors.text} verticalAlign="top">{selectedRequestOption.clientName}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.clientOrg && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Store</Td>
                      <Td color={colors.text} verticalAlign="top">{selectedRequestOption.clientOrg}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.categories && selectedRequestOption.categories.length > 0 && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Categories</Td>
                      <Td color={colors.text} verticalAlign="top">
                        <HStack spacing={2} flexWrap="wrap">
                          {selectedRequestOption.categories.map((cat, idx) => (
                            <Box key={idx} px={2} py={1} bg="gray.100" color={colors.text} borderRadius="md" fontSize="xs">
                              {cat}
                            </Box>
                          ))}
                        </HStack>
                      </Td>
                    </Tr>
                  )}
                  {selectedRequestOption.description && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Description</Td>
                      <Td color={colors.text} verticalAlign="top" whiteSpace="pre-wrap">{selectedRequestOption.description}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.requestedQuantity !== undefined && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Requested Quantity</Td>
                      <Td color={colors.text} verticalAlign="top">{selectedRequestOption.requestedQuantity}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.preferredScheduleAt && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Preferred Schedule At</Td>
                      <Td color={colors.text} verticalAlign="top">{fmtDatetimeLocal(selectedRequestOption.preferredScheduleAt)}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.contactName && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Contact Name</Td>
                      <Td color={colors.text} verticalAlign="top">{selectedRequestOption.contactName}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.contactEmail && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Contact Email</Td>
                      <Td color={colors.text} verticalAlign="top">{selectedRequestOption.contactEmail}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.contactNumber && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Contact Number</Td>
                      <Td color={colors.text} verticalAlign="top">{selectedRequestOption.contactNumber}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.serialNumber && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Serial Number</Td>
                      <Td color={colors.text} verticalAlign="top">{selectedRequestOption.serialNumber}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.unitModel && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Unit Model</Td>
                      <Td color={colors.text} verticalAlign="top">{selectedRequestOption.unitModel}</Td>
                    </Tr>
                  )}
                  {selectedRequestOption.serviceType && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Service Type</Td>
                      <Td color={colors.text} verticalAlign="top">
                        {selectedRequestOption.serviceParent && (
                          <Text as="span" color={colors.textSecondary} fontSize="xs" mr={2}>{selectedRequestOption.serviceParent} → </Text>
                        )}
                        {selectedRequestOption.serviceType}
                      </Td>
                    </Tr>
                  )}
                  {selectedRequestOption.submittedAt && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Submitted At</Td>
                      <Td color={colors.text} verticalAlign="top">{fmtDatetimeLocal(selectedRequestOption.submittedAt)}</Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </PreviewBlock>
          )}

          {/* Ticket Details: ticket fields, store, assets, work permit, agent, FEs, schedule, notes */}
          <PreviewBlock title="Ticket Details">
            <VStack align="stretch" spacing={4}>
              {/* Ticket fields */}
              <Table variant="simple" size="sm">
                <Tbody>
                  {form.subject && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%">Subject</Td>
                      <Td color={colors.text}>{form.subject}</Td>
                    </Tr>
                  )}
                  {(form.severity ?? form.priority) && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%">Severity</Td>
                      <Td color={colors.text}>Severity {form.severity ?? form.priority}</Td>
                    </Tr>
                  )}
                  {form.source && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%">Source</Td>
                      <Td color={colors.text}>{form.source}</Td>
                    </Tr>
                  )}
                  {form.issueDescription && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">Issue Description</Td>
                      <Td color={colors.text} whiteSpace="pre-wrap">{form.issueDescription}</Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>

              {/* Complete Store Information */}
              {selectedStoreOption && (
                <>
                  <Text fontWeight="600" color={colors.textSecondary} fontSize="xs" textTransform="uppercase">Store</Text>
                  <Table variant="simple" size="sm">
                    <Tbody>
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%">Store Code</Td>
                        <Td color={colors.text}>{selectedStoreOption.storeCode || form.storeCode}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%">Store Name</Td>
                        <Td color={colors.text}>{selectedStoreOption.storeName}</Td>
                      </Tr>
                      {selectedStoreOption.branchAddress && (
                        <Tr>
                          <Td fontWeight="600" color={colors.textSecondary} width="30%">Address</Td>
                          <Td color={colors.text}>{selectedStoreOption.branchAddress}</Td>
                        </Tr>
                      )}
                      {selectedStoreOption.storeEmail && (
                        <Tr>
                          <Td fontWeight="600" color={colors.textSecondary} width="30%">Email</Td>
                          <Td color={colors.text}>{selectedStoreOption.storeEmail}</Td>
                        </Tr>
                      )}
                      {selectedStoreOption.storeContact && (
                        <Tr>
                          <Td fontWeight="600" color={colors.textSecondary} width="30%">Contact</Td>
                          <Td color={colors.text}>{selectedStoreOption.storeContact}</Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </>
              )}

              {/* Assets */}
              {selectedAssets && selectedAssets.length > 0 && (
                <>
                  <Text fontWeight="600" color={colors.textSecondary} fontSize="xs" textTransform="uppercase">Assets</Text>
                  <Box border="1px solid" borderColor={colors.borderLight} borderRadius="md" maxH="300px" overflowY="auto">
                    <Table size="sm" variant="simple">
                      <Thead position="sticky" top={0} bg={colors.cardBg} zIndex={1}>
                        <Tr>
                          <Th>Tag Number</Th>
                          <Th>Model</Th>
                          <Th>Serial Number</Th>
                          <Th>Brand</Th>
                          <Th>Category</Th>
                          <Th>Function</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {selectedAssets.map((asset, index) => (
                          <Tr key={asset.id || `asset-${index}`}>
                            <Td><Text fontSize="sm" color={colors.text}>{asset.tag_number || '-'}</Text></Td>
                            <Td><Text fontSize="sm" color={colors.text}>{asset.model || '-'}</Text></Td>
                            <Td><Text fontSize="sm" color={colors.text}>{asset.serial_number || '-'}</Text></Td>
                            <Td><Text fontSize="sm" color={colors.text}>{asset.brand_name || '-'}</Text></Td>
                            <Td><Text fontSize="sm" color={colors.text}>{asset.category_name || '-'}</Text></Td>
                            <Td><Text fontSize="sm" color={colors.text}>{asset.function || '-'}</Text></Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}

              {/* Agent and Field Engineers */}
              <Table variant="simple" size="sm">
                <Tbody>
                  {selectedAgentOption && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%">Agent</Td>
                      <Td color={colors.text}>
                        {[selectedAgentOption.first_name, selectedAgentOption.last_name].filter(Boolean).join(' ') || agentLabel(selectedAgentOption)}
                        {selectedAgentOption.user_email && (
                          <Text as="span" fontSize="xs" color={colors.textSecondary} ml={2}>
                            ({selectedAgentOption.user_email})
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  )}
                  {(form.primaryEngineerId || editTicketMeta?.primaryEngineer) && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                        Primary Engineer
                      </Td>
                      <Td color={colors.text} verticalAlign="top">
                        <VStack align="stretch" spacing={0} gap={1}>
                          <Text>{primaryEngineerDisplayName ?? '—'}</Text>
                          {primaryEngineerDetails?.user_email && (
                            <Text fontSize="sm" color={colors.textSecondary}>{primaryEngineerDetails.user_email}</Text>
                          )}
                          {primaryEngineerDetails?.cluster && (
                            <Text fontSize="xs" color={colors.textSecondary}>Cluster: {primaryEngineerDetails.cluster}</Text>
                          )}
                          {(primaryEngineerDetails?.workload != null && primaryEngineerDetails?.workload !== undefined) && (
                            <Text fontSize="xs" color={colors.textSecondary}>Workload: {primaryEngineerDetails.workload} active requests</Text>
                          )}
                          {primaryEngineerDetails?.match_reasons && primaryEngineerDetails.match_reasons.length > 0 && (
                            <Text fontSize="xs" color={colors.textSecondary}>Match reasons: {primaryEngineerDetails.match_reasons.join(', ')}</Text>
                          )}
                          {primaryEngineerDetails?.certifications && primaryEngineerDetails.certifications.length > 0 && (
                            <Text fontSize="xs" color={colors.textSecondary}>Certifications: {primaryEngineerDetails.certifications.join(', ')}</Text>
                          )}
                          {primaryEngineerDetails?.address && (
                            <Text fontSize="xs" color={colors.textSecondary} noOfLines={2}>{primaryEngineerDetails.address}</Text>
                          )}
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                  {(form.backupEngineerId || editTicketMeta?.backupEngineer) && (
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                        Backup Engineer
                      </Td>
                      <Td color={colors.text} verticalAlign="top">
                        <VStack align="stretch" spacing={0} gap={1}>
                          <Text>{backupEngineerDisplayName ?? '—'}</Text>
                          {backupEngineerDetails?.user_email && (
                            <Text fontSize="sm" color={colors.textSecondary}>{backupEngineerDetails.user_email}</Text>
                          )}
                          {backupEngineerDetails?.cluster && (
                            <Text fontSize="xs" color={colors.textSecondary}>Cluster: {backupEngineerDetails.cluster}</Text>
                          )}
                          {(backupEngineerDetails?.workload != null && backupEngineerDetails?.workload !== undefined) && (
                            <Text fontSize="xs" color={colors.textSecondary}>Workload: {backupEngineerDetails.workload} active requests</Text>
                          )}
                          {backupEngineerDetails?.match_reasons && backupEngineerDetails.match_reasons.length > 0 && (
                            <Text fontSize="xs" color={colors.textSecondary}>Match reasons: {backupEngineerDetails.match_reasons.join(', ')}</Text>
                          )}
                          {backupEngineerDetails?.certifications && backupEngineerDetails.certifications.length > 0 && (
                            <Text fontSize="xs" color={colors.textSecondary}>Certifications: {backupEngineerDetails.certifications.join(', ')}</Text>
                          )}
                          {backupEngineerDetails?.address && (
                            <Text fontSize="xs" color={colors.textSecondary} noOfLines={2}>{backupEngineerDetails.address}</Text>
                          )}
                        </VStack>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>

              {/* Agent and Field Engineers - no Schedule & Notes in preview */}
            </VStack>
          </PreviewBlock>

          {/* Actions: Back + Save as Draft + Submit Ticket */}
          <HStack justify="space-between" pt={2} spacing={3} flexWrap="wrap">
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
            <HStack spacing={3}>
              <Button
                leftIcon={<Save size={16} />}
                variant="outline"
                colorScheme="blue"
                onClick={() => handleSubmit(false)}
                isLoading={savingMode === 'draft'}
                loadingText="Saving..."
              >
                Save as Draft
              </Button>
              <Button
                leftIcon={<Save size={16} />}
                bg="red.700"
                _hover={{ bg: 'red.800' }}
                color="white"
                onClick={() => handleSubmit(true)}
                isLoading={savingMode === 'submit'}
                loadingText={isEditMode ? 'Submitting...' : 'Creating...'}
              >
                Submit Ticket
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </Box>
    )
  }

  return (
    <Box
      p={{ base: 4, md: 6 }}
      w="100%"
      minW={0}
      bg={pageBg} 
      minH="100vh"
    >
      {/* Page Title
      <HStack justify="space-between" mb={4} align="center">
        <Heading size="md" color={colors.heading}>
          Tickets
        </Heading>
      </HStack>
      */}

      {/* Go Back Button 
      <Button
        variant="link"
        leftIcon={<ArrowLeft size={16} />}
        onClick={() => navigate(-1)}
        color={colors.text}
        mb={4}
        _hover={{ textDecoration: 'none', color: colors.linkHover }}
        alignSelf="flex-start"
      >
        Go Back
      </Button>
      */}


      <Divider mb={6} />

      {Object.keys(errors).length > 0 && (
        <Alert status="error" borderRadius="md" mb={5}>
          <AlertIcon />
          <AlertDescription>Please fix the highlighted fields below.</AlertDescription>
        </Alert>
      )}

      <VStack spacing={6} align="stretch">
        <Section title="Select request *" sectionBg={sectionBg}>
          <FormControl isInvalid={!!errors.requestId}>
            <HStack align="flex-start" spacing={3}>
              <Box position="relative" flex={1} minW={0}>
                <Input
                ref={requestSearchInputRef}
                size="sm"
                placeholder="Search request..."
                value={requestSearchTerm}
                onChange={handleRequestSearchChange}
                onFocus={() => {
                  if (requestSearchTerm.length >= 3 && requestSearchResults.length === 0) {
                    searchRequests(requestSearchTerm)
                  }
                }}
                autoComplete="off"
                {...inputBaseProps}
              />
              {isRequestSearching && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  mt={1}
                  p={2}
                  zIndex={1000}
                  boxShadow="md"
                >
                  <HStack justify="center">
                    <Spinner size="sm" />
                    <Text fontSize="sm">Searching...</Text>
                  </HStack>
                </Box>
              )}
              {requestSearchResults.length > 0 && !isRequestSearching && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  mt={1}
                  maxH="300px"
                  overflowY="auto"
                  zIndex={1000}
                  boxShadow="md"
                >
                  {requestSearchResults.map((request) => (
                    <Box
                      key={request.id}
                      p={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleRequestSelect(request)
                      }}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                      _last={{ borderBottom: 'none' }}
                    >
                      <Text fontSize="sm" fontWeight="500">
                        {request.id} - {request.clientName}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {request.clientOrg}
                        {request.serialNumber && ` | SN: ${request.serialNumber}`}
                        {request.unitModel && ` | ${request.unitModel}`}
                      </Text>
                      {request.issueDescription && (
                        <Text fontSize="xs" color="gray.500" mt={1} noOfLines={2}>
                          {request.issueDescription}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              </Box>
              <Button
                size="sm"
                leftIcon={<Plus size={16} />}
                variant="outline"
                colorScheme="blue"
                onClick={onAddRequestModalOpen}
                flexShrink={0}
              >
                Add
              </Button>
            </HStack>
            {selectedRequestOption && (
              <Box
                ref={selectedRequestDetailsRef}
                mt={2}
                p={4}
                bg="white"
                borderRadius="md"
                borderWidth="1px"
                borderColor={colors.border || 'gray.200'}
                boxShadow="sm"
              >
                <Text fontSize="xs" fontWeight="700" color={colors.textSecondary || 'gray.600'} mb={3} textTransform="uppercase" letterSpacing="wider">
                  Selected Request Details
                </Text>
                <Table variant="unstyled" size="sm">
                  <Tbody>
                    {[
                      { label: 'Request ID', value: selectedRequestOption.id },
                      { label: 'Store', value: selectedRequestOption.clientOrg },
                      {
                        label: 'Categories',
                        node: selectedRequestOption.categories?.length ? (
                          <HStack spacing={2} flexWrap="wrap">
                            {selectedRequestOption.categories.map((cat, idx) => (
                              <Box key={idx} px={2} py={1} bg="red.700" color="white" borderRadius="md" fontSize="xs">
                                {cat}
                              </Box>
                            ))}
                          </HStack>
                        ) : null,
                      },
                      { label: 'Description', value: selectedRequestOption.description, preWrap: true },
                      {
                        label: 'Requested Quantity',
                        value: selectedRequestOption.requestedQuantity !== undefined ? String(selectedRequestOption.requestedQuantity) : undefined,
                      },
                      {
                        label: 'Preferred Schedule At',
                        value: selectedRequestOption.preferredScheduleAt ? fmtDatetimeLocal(selectedRequestOption.preferredScheduleAt) : undefined,
                      },
                      { label: 'Contact Name', value: selectedRequestOption.contactName },
                      { label: 'Contact Email', value: selectedRequestOption.contactEmail },
                      { label: 'Contact Number', value: selectedRequestOption.contactNumber },
                      {
                        label: 'Preferred Contact At',
                        value: selectedRequestOption.preferredContactAt ? fmtDatetimeLocal(selectedRequestOption.preferredContactAt) : undefined,
                      },
                      {
                        label: 'Service Type',
                        node: (selectedRequestOption.serviceType || selectedRequestOption.serviceParent) ? (
                          <Text as="span" color={colors.text}>
                            {selectedRequestOption.serviceParent && selectedRequestOption.serviceType
                              ? `${selectedRequestOption.serviceParent} → ${selectedRequestOption.serviceType}`
                              : (selectedRequestOption.serviceType || selectedRequestOption.serviceParent)}
                          </Text>
                        ) : null,
                      },
                      {
                        label: 'Submitted At',
                        value: selectedRequestOption.submittedAt ? fmtDatetimeLocal(selectedRequestOption.submittedAt) : undefined,
                      },
                      {
                        label: 'Acknowledged At',
                        value: selectedRequestOption.acknowledgedAt ? fmtDatetimeLocal(selectedRequestOption.acknowledgedAt) : undefined,
                      },
                    ].map((row, idx) => {
                      const value = 'value' in row ? row.value : undefined
                      const node = 'node' in row ? row.node : undefined
                      const content = node ?? value
                      if (content == null || content === '') return null
                      return (
                        <Tr key={idx} _even={{ bg: 'gray.50' }}>
                          <Td fontWeight="600" color={colors.textSecondary || 'gray.600'} width="30%" verticalAlign="top" py={2}>
                            {row.label}
                          </Td>
                          <Td color={colors.text} verticalAlign="top" py={2} whiteSpace={'preWrap' in row && row.preWrap ? 'pre-wrap' : undefined}>
                            {content}
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
            <FormHelperText>Type at least 3 characters to search for acknowledged requests, or use Add to create a ticket request manually from another source (e.g. email, phone).</FormHelperText>
            <FormErrorMessage>{errors.requestId?.[0]}</FormErrorMessage>
          </FormControl>
        </Section>

        <CreateTicketRequestModal
          isOpen={isAddRequestModalOpen}
          onClose={onAddRequestModalClose}
          onCreated={(request) => {
            handleRequestSelect(request)
            onAddRequestModalClose()
          }}
        />

        {/* Ticket Details */}
        <Section title="Ticket Details" sectionBg={sectionBg}>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Ticket ID</FormLabel>
              <Input
                size="sm"
                value={form.ticketId || 'Auto-generated'}
                isReadOnly
                {...inputBaseProps}
              />
            </FormControl>
            <FormControl isInvalid={!!errors.subject} isRequired>
              <FormLabel fontSize="sm">Subject</FormLabel>
              <Input
                size="sm"
                value={form.subject}
                onChange={(e) => updateField('subject', e.target.value)}
                placeholder="Request ID | Contact Name | Client/Organization"
                {...inputBaseProps}
              />
              <FormErrorMessage>{errors.subject?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mt={4}>
            <FormControl isInvalid={!!errors.source} isRequired>
              <FormLabel fontSize="sm">Source</FormLabel>
              <Input
                size="sm"
                value={form.source}
                isReadOnly
                placeholder="From request (e.g. portal, voice_ai, email)"
                {...inputBaseProps}
              />
              <FormErrorMessage>{errors.source?.[0]}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!(errors.severity ?? errors.priority)} isRequired>
              <FormLabel fontSize="sm">Severity</FormLabel>
              <Select
                size="sm"
                value={form.severity ?? form.priority ?? '3'}
                onChange={(e) => {
                  updateField('severity', e.target.value)
                  updateField('priority', e.target.value)
                }}
                placeholder="Select severity"
                {...inputBaseProps}
              >
                {severityOptions.map((s) => (
                  <option key={s} value={s}>Severity {s}</option>
                ))}
              </Select>
              <FormErrorMessage>{(errors.severity ?? errors.priority)?.[0]}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.supportType} isRequired>
              <FormLabel fontSize="sm">Support Type</FormLabel>
              <Select
                size="sm"
                value={form.supportType ?? ''}
                onChange={(e) => updateField('supportType', e.target.value)}
                placeholder="Select support type"
                {...inputBaseProps}
              >
                {supportTypeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
              <FormErrorMessage>{errors.supportType?.[0]}</FormErrorMessage>
            </FormControl>
          </Grid>

          <FormControl mt={4} isInvalid={!!errors.issueDescription} isRequired>
            <FormLabel fontSize="sm">Description</FormLabel>
            <Textarea
              size="sm"
              value={form.issueDescription}
              onChange={(e) => updateField('issueDescription', e.target.value)}
              placeholder="Type here..."
              minH="96px"
              {...inputBaseProps}
            />
            <FormErrorMessage>{errors.issueDescription?.[0]}</FormErrorMessage>
          </FormControl>

          {/* Store (not required) - full width */}
          <FormControl mt={4} w="100%">
            <FormLabel fontSize="sm">Store</FormLabel>
            <Box position="relative" w="100%">
              <Input
                ref={storeSearchInputRef}
                size="sm"
                value={storeSearchTerm}
                onChange={handleStoreSearchChange}
                placeholder="Search stores (min 3 characters)..."
                w="100%"
                {...inputBaseProps}
              />
              {isStoreSearching && (
                <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
                  <Spinner size="sm" color={colors.primary} />
                </Box>
              )}
              {storeSearchResults.length > 0 && !isStoreSearching && (
                <Box
                  position="absolute"
                  zIndex={10}
                  w="100%"
                  mt={1}
                  bg="white"
                  border="1px solid"
                  borderColor={colors.borderLight}
                  borderRadius="md"
                  boxShadow="md"
                  maxH="240px"
                  overflowY="auto"
                >
                  {storeSearchResults.map((store) => (
                    <Box
                      key={store.storeCode}
                      px={4}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => handleStoreSelect(store)}
                      borderBottom="1px solid"
                      borderColor={colors.borderLight}
                      _last={{ borderBottom: 'none' }}
                    >
                      <Text fontWeight="600" fontSize="sm" color={colors.text}>
                        [{store.storeCode}] - {store.storeName}
                      </Text>
                      {store.businessUnitName && (
                        <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                          {store.businessUnitName}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            {selectedStoreOption && (
              <Box mt={2} p={4} bg={colors.sectionBg || 'gray.50'} borderRadius="md" borderWidth="1px" borderColor={colors.border || 'gray.200'}>
                <Text fontSize="xs" fontWeight="600" color={colors.textSecondary} mb={3} textTransform="uppercase" letterSpacing="wider">
                  Selected Store Details
                </Text>
                <Table variant="simple" size="sm">
                  <Tbody>
                    {/* Store Code */}
                    <Tr>
                      <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                        Store Code
                      </Td>
                      <Td color={colors.text} verticalAlign="top">
                        {selectedStoreOption.storeCode}
                      </Td>
                    </Tr>

                    {/* Store Name */}
                    {selectedStoreOption.storeName && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Store Name
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.storeName}
                        </Td>
                      </Tr>
                    )}

                    {/* Business Unit */}
                    {selectedStoreOption.businessUnitName && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Business Unit
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.businessUnitName}
                        </Td>
                      </Tr>
                    )}

                    {/* Branch Address */}
                    {selectedStoreOption.branchAddress && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Address
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.branchAddress}
                        </Td>
                      </Tr>
                    )}

                    {/* Store Email */}
                    {selectedStoreOption.storeEmail && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Email
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.storeEmail}
                        </Td>
                      </Tr>
                    )}

                    {/* Store Contact */}
                    {selectedStoreOption.storeContact && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Contact Number
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.storeContact}
                        </Td>
                      </Tr>
                    )}

                    {/* Alternate Contact */}
                    {selectedStoreOption.alternateContact && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Alternate Contact
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.alternateContact}
                        </Td>
                      </Tr>
                    )}

                    {/* Contact Person */}
                    {selectedStoreOption.amName && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Contact Person
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.amName}
                        </Td>
                      </Tr>
                    )}

                    {/* Store Hours */}
                    {selectedStoreOption.storeHours && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Store Hours
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.storeHours}
                        </Td>
                      </Tr>
                    )}

                    {/* Ownership Type */}
                    {selectedStoreOption.ownershipType && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Ownership Type
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.ownershipType}
                        </Td>
                      </Tr>
                    )}

                    {/* Location Address */}
                    {selectedStoreOption.locationAddress && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Location Address
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.locationAddress}
                        </Td>
                      </Tr>
                    )}

                    {/* Normalized Address */}
                    {selectedStoreOption.normalizedAddress && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Normalized Address
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.normalizedAddress}
                        </Td>
                      </Tr>
                    )}

                    {/* Latitude */}
                    {selectedStoreOption.latitude !== undefined && selectedStoreOption.latitude !== null && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Latitude
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.latitude.toFixed(8)}
                        </Td>
                      </Tr>
                    )}

                    {/* Longitude */}
                    {selectedStoreOption.longitude !== undefined && selectedStoreOption.longitude !== null && (
                      <Tr>
                        <Td fontWeight="600" color={colors.textSecondary} width="30%" verticalAlign="top">
                          Longitude
                        </Td>
                        <Td color={colors.text} verticalAlign="top">
                          {selectedStoreOption.longitude.toFixed(8)}
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
            <FormHelperText fontSize="xs" color={colors.textMuted} mt={1} mb={0}>
              Leave blank if no store is involved in the ticket creation.
            </FormHelperText>
          </FormControl>

          {/* Asset list */}
          {(
          <>
          <FormControl mt={4} w="100%">
            <FormLabel fontSize="sm">Asset list</FormLabel>
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              w="full"
              onClick={onAssetModalOpen}
              leftIcon={<Icon as={Plus as any} boxSize="16px" />}
            >
              Add Item
            </Button>
            {selectedAssets.length > 0 && (
              <Box mt={3} border="1px solid" borderColor={colors.borderLight} borderRadius="md" overflow="hidden">
                <Table size="sm" variant="simple">
                  <Thead bg={colors.cardBg}>
                    <Tr>
                      <Th width="50px"></Th>
                      <Th>Tag Number</Th>
                      <Th>Model</Th>
                      <Th>Serial Number</Th>
                      <Th>Brand</Th>
                      <Th>Category</Th>
                      <Th>Function</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {selectedAssets.map((asset) => (
                      <Tr key={asset.id} _hover={{ bg: 'gray.50' }}>
                        <Td>
                          <IconButton
                            aria-label="Remove asset"
                            icon={<X size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleRemoveAsset(asset.id)}
                          />
                        </Td>
                        <Td>
                          <Text fontWeight="600" fontSize="sm" color={colors.text}>
                            {asset.tag_number || '-'}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={colors.text}>
                            {asset.model || '-'}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={colors.text}>
                            {asset.serial_number || '-'}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={colors.text}>
                            {asset.brand_name || '-'}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={colors.text}>
                            {asset.category_name || '-'}
                          </Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={colors.text}>
                            {asset.function || '-'}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
            <FormHelperText fontSize="xs" color={colors.textMuted} mt={1} mb={0}>
              Leave blank if no item needs to be added.
            </FormHelperText>
          </FormControl>

          {/* Asset Search Modal */}
          <Modal isOpen={isAssetModalOpen} onClose={onAssetModalClose} size="6xl">
            <ModalOverlay />
            <ModalContent maxW="90vw">
              <ModalHeader>
                <HStack justify="space-between" align="center" width="100%" pr={10}>
                  <Text>Search Assets</Text>
                  <Button
                    size="sm"
                    colorScheme="green"
                    leftIcon={<Icon as={FilePlus as any} boxSize="16px" />}
                    onClick={onCreateAssetModalOpen}
                  >
                    Create New Asset
                  </Button>
                </HStack>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <InputGroup>
                      <Input
                        ref={assetSearchInputRef}
                        placeholder="Search by tag number, model, serial number, store code, store name..."
                        value={assetSearchTerm}
                        onChange={handleAssetSearchChange}
                        {...inputBaseProps}
                      />
                      <InputRightElement>
                        {isAssetSearching ? (
                          <Spinner size="sm" color={colors.primary} />
                        ) : (
                          <Search size={16} color={colors.textSecondary} />
                        )}
                      </InputRightElement>
                    </InputGroup>
                    <FormHelperText fontSize="xs" color={colors.textMuted} mt={1}>
                      Type at least 3 characters to search
                      {selectedStoreOption && (
                        <Text as="span" fontWeight="600" ml={1}>
                          • Filtered by store: {selectedStoreOption.storeName}
                        </Text>
                      )}
                    </FormHelperText>
                  </FormControl>

                  {assetSearchResults.length > 0 && (
                    <Box
                      border="1px solid"
                      borderColor={colors.borderLight}
                      borderRadius="md"
                      maxH="500px"
                      overflowY="auto"
                    >
                      <Table size="sm" variant="simple">
                        <Thead position="sticky" top={0} bg={colors.cardBg} zIndex={1}>
                          <Tr>
                            <Th width="50px">
                              <Checkbox
                                isChecked={assetSearchResults.length > 0 && assetSearchResults.every(asset => selectedAssets.some(a => a.id === asset.id))}
                                isIndeterminate={
                                  assetSearchResults.some(asset => selectedAssets.some(a => a.id === asset.id)) &&
                                  !assetSearchResults.every(asset => selectedAssets.some(a => a.id === asset.id))
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Select all visible assets that aren't already selected
                                    const newAssets = assetSearchResults.filter(
                                      asset => !selectedAssets.some(a => a.id === asset.id)
                                    )
                                    const updatedAssets = [...selectedAssets, ...newAssets]
                                    setSelectedAssets(updatedAssets)
                                    setForm(prev => ({ ...prev, assets: updatedAssets }))
                                  } else {
                                    // Deselect all visible assets
                                    const updatedAssets = selectedAssets.filter(
                                      asset => !assetSearchResults.some(a => a.id === asset.id)
                                    )
                                    setSelectedAssets(updatedAssets)
                                    setForm(prev => ({ ...prev, assets: updatedAssets }))
                                  }
                                }}
                              />
                            </Th>
                            <Th>Tag Number</Th>
                            <Th>Model</Th>
                            <Th>Serial Number</Th>
                            <Th>Brand</Th>
                            <Th>Category</Th>
                            <Th>Function</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {assetSearchResults.map((asset) => {
                            const isSelected = selectedAssets.some(a => a.id === asset.id)
                            return (
                              <Tr
                                key={asset.id}
                                _hover={{ bg: 'gray.50' }}
                                cursor="pointer"
                                onClick={() => handleAssetSelect(asset)}
                              >
                                <Td onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    isChecked={isSelected}
                                    onChange={(e) => {
                                      e.stopPropagation()
                                      if (e.target.checked) {
                                        handleAssetSelect(asset)
                                      } else {
                                        handleRemoveAsset(asset.id)
                                      }
                                    }}
                                  />
                                </Td>
                                <Td>
                                  <Text fontWeight="600" fontSize="sm" color={colors.text}>
                                    {asset.tag_number || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.model || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.serial_number || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.brand_name || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.category_name || '-'}
                                  </Text>
                                </Td>
                                <Td>
                                  <Text fontSize="sm" color={colors.text}>
                                    {asset.function || '-'}
                                  </Text>
                                </Td>
                              </Tr>
                            )
                          })}
                        </Tbody>
                      </Table>
                    </Box>
                  )}

                  {assetSearchTerm.length >= 3 && assetSearchResults.length === 0 && !isAssetSearching && (
                    <Text fontSize="sm" color={colors.textSecondary} textAlign="center" py={4}>
                      No assets found matching "{assetSearchTerm}"
                    </Text>
                  )}
                </VStack>
              </ModalBody>
              <ModalFooter>
                <HStack spacing={2}>
                  <Text fontSize="sm" color={colors.textSecondary}>
                    {selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''} selected
                  </Text>
                  <Button colorScheme="blue" onClick={onAssetModalClose}>
                    Done
                  </Button>
                </HStack>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Create Asset Modal */}
          <CreateAssetModal
            isOpen={isCreateAssetModalOpen}
            onClose={onCreateAssetModalClose}
            onSuccess={(newAsset) => {
              // After creating asset, add it to selected assets
              handleAssetSelect(newAsset)
              
              // Also add it to the search results so it appears in the modal table
              setAssetSearchResults(prev => {
                // Check if asset is already in the results
                if (prev.some(a => a.id === newAsset.id)) {
                  return prev
                }
                return [newAsset, ...prev]
              })
              
              onCreateAssetModalClose()
            }}
            clientCode={selectedRequestOption?.clientCode}
          />
          </>
          )}

        </Section>

        {/* Agent Search (Helpdesk users) - required; always show when editing so user can see/change assignment */}
        <Section title="Agent *" sectionBg={sectionBg}>
          {isEditMode && !selectedAgentOption && (
            <Text fontSize="sm" color={colors.textSecondary} mb={2}>
              No agent assigned
            </Text>
          )}
          <FormControl isInvalid={!!errors.agentId} isRequired>
            <Box position="relative">
              <Input
                ref={agentSearchInputRef}
                size="sm"
                value={agentSearchTerm}
                onChange={handleAgentSearchChange}
                onFocus={() => {
                  if (agentSearchTerm.length >= 2 && agentSearchResults.length === 0) {
                    searchAgents(agentSearchTerm)
                  }
                }}
                placeholder="Search by name or email (min 2 characters)..."
                autoComplete="off"
                {...inputBaseProps}
              />
              {isAgentSearching && (
                <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
                  <Spinner size="sm" color={colors.primary} />
                </Box>
              )}
              {agentSearchResults.length > 0 && !isAgentSearching && (
                <Box
                  position="absolute"
                  zIndex={10}
                  w="100%"
                  mt={1}
                  bg="white"
                  border="1px solid"
                  borderColor={colors.borderLight}
                  borderRadius="md"
                  boxShadow="md"
                  maxH="240px"
                  overflowY="auto"
                >
                  {agentSearchResults.map((agent) => (
                    <Box
                      key={agent.id}
                      px={4}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      onClick={() => handleAgentSelect(agent)}
                      borderBottom="1px solid"
                      borderColor={colors.borderLight}
                      _last={{ borderBottom: 'none' }}
                    >
                      <Text fontWeight="600" fontSize="sm" color={colors.text}>
                        {agentLabel(agent)}
                      </Text>
                      {agent.user_email && (
                        <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                          {agent.user_email}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <FormHelperText fontSize="xs" color={colors.textMuted} mt={1} mb={0}>
              Assign to agent (search Helpdesk users).
            </FormHelperText>
            {currentUser && (
              <Checkbox
                mt={3}
                isChecked={assignToMe}
                onChange={handleAssignToMeChange}
                colorScheme="blue"
                size="sm"
              >
                <Text as="span" fontSize="sm">Assign to me</Text>
              </Checkbox>
            )}
            <FormErrorMessage>{errors.agentId?.[0]}</FormErrorMessage>
          </FormControl>
        </Section>

        {/* Bottom actions: Next goes to summary for both Create and Edit */}
        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={() => (onCancel ? onCancel() : navigate(-1))}>
            Cancel
          </Button>
          <Button
            bg="blue.600"
            _hover={{ bg: 'blue.700' }}
            color="white"
            onClick={handleNext}
          >
            Next
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
